import React, { useState, useRef, useEffect } from 'react';
import { FlowNode } from '../types/zapier';
import {
  highlightVariables,
  resolveVariableSource,
  DetectedVariable,
  detectDataType,
} from '../utils/variableParser';
import { VariableTooltip } from './VariableTooltip';
import { useStore } from '../store/useStore';

interface VariableHighlighterProps {
  text: string;
  currentNode: FlowNode;
  allNodes: FlowNode[];
  className?: string;
  /** The original config value (for data type detection) */
  configValue?: any;
}

/**
 * Component that highlights variables in text and shows tooltips on hover
 * Displays {{variable}} patterns with visual styling and interactive tooltips
 */
export const VariableHighlighter: React.FC<VariableHighlighterProps> = ({
  text,
  currentNode,
  allNodes,
  className = '',
  configValue,
}) => {
  const { isUnlocked, setHoveredVariableConnection } = useStore();
  const [hoveredVariable, setHoveredVariable] = useState<{
    variable: DetectedVariable;
    element: HTMLElement;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Parse text into segments
  const segments = highlightVariables(text);

  // Handle variable hover
  const handleVariableHover = (
    variable: DetectedVariable | undefined,
    event: React.MouseEvent<HTMLSpanElement>
  ) => {
    if (!variable) {
      setHoveredVariable(null);
      setHoveredVariableConnection(null);
      return;
    }

    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();

    // Position tooltip below the variable
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    });

    setHoveredVariable({ variable, element: target });

    // Set hover state for visual connections in flow graph
    const source = resolveVariableSource(variable, currentNode, allNodes);
    if (source.sourceNode) {
      // Detect data type from the config value
      const dataType = configValue ? detectDataType(configValue) : 'unknown';

      setHoveredVariableConnection({
        sourceNodeId: source.sourceNode.id,
        targetNodeId: currentNode.id,
        dataType,
      });
    }
  };

  const handleVariableLeave = () => {
    setHoveredVariable(null);
    setHoveredVariableConnection(null);
  };

  // Close tooltip on scroll or click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hoveredVariable && !hoveredVariable.element.contains(event.target as Node)) {
        setHoveredVariable(null);
        setHoveredVariableConnection(null);
      }
    };

    const handleScroll = () => {
      if (hoveredVariable) {
        const rect = hoveredVariable.element.getBoundingClientRect();
        setTooltipPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [hoveredVariable, setHoveredVariableConnection]);

  // Render the variable source tooltip
  const renderTooltip = () => {
    if (!hoveredVariable) return null;

    const source = resolveVariableSource(
      hoveredVariable.variable,
      currentNode,
      allNodes
    );

    return (
      <div
        className="fixed z-50"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
        }}
      >
        <VariableTooltip
          source={source}
          isUnlocked={isUnlocked}
          variable={{
            fullMatch: hoveredVariable.variable.fullMatch,
            path: hoveredVariable.variable.path,
            fieldPath: hoveredVariable.variable.fieldPath,
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div ref={containerRef} className={`font-mono break-all ${className}`}>
        {segments.map((segment, index) => {
          if (segment.type === 'variable' && segment.variable) {
            const source = resolveVariableSource(segment.variable, currentNode, allNodes);
            const isResolvable = source.isResolvable;

            return (
              <span
                key={index}
                className={`
                  inline-block px-1.5 py-0.5 rounded cursor-help transition-colors
                  ${isResolvable
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                    : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                  }
                  ${hoveredVariable?.variable === segment.variable ? 'ring-2 ring-blue-500' : ''}
                `}
                onMouseEnter={(e) => handleVariableHover(segment.variable, e)}
                onMouseLeave={handleVariableLeave}
                title={isResolvable ? 'Click to see source' : 'Unresolved variable'}
              >
                {segment.content}
              </span>
            );
          }

          return (
            <span key={index} className="text-foreground">
              {segment.content}
            </span>
          );
        })}
      </div>

      {/* Tooltip portal */}
      {renderTooltip()}
    </>
  );
};
